/**
 * Calendar event types for the unified calendar system.
 * Maps directly to the backend calendar_events table fields.
 */

export interface CalendarEvent {
  id: number;
  uuid: string;
  title: string;
  description?: string;
  location?: string;
  color?: string;
  start_time: string;         // ISO datetime
  end_time?: string;          // ISO datetime
  all_day: boolean;
  event_type: CalendarEventType;
  source_type?: CalendarSourceType;
  source_id?: number;
  source_uuid?: string;
  recurrence?: string;        // 'daily' | 'weekly' | 'monthly' | 'yearly' | null
  reminder_minutes?: number;
  ical_uid?: string;
  status: CalendarEventStatus;
  visibility: 'private' | 'public' | 'organization';
  shared_with?: CalendarCollaborator[];
  collaborators?: CalendarCollaborator[];
  tags?: string[];
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
  created_by_user_id?: number;
}

export type CalendarEventType =
  | 'task' | 'task_deadline'
  | 'matter' | 'matter_deadline'
  | 'project' | 'project_deadline'
  | 'hearing'
  | 'meeting'
  | 'consultation'
  | 'filing_deadline'
  | 'statute_of_limitations'
  | 'client_meeting'
  | 'court_date'
  | 'reminder'
  | 'custom';

export type CalendarSourceType = 'task' | 'matter' | 'project' | 'client' | 'manual';

export type CalendarEventStatus = 'active' | 'completed' | 'cancelled' | 'tentative';

export interface CalendarCollaborator {
  user_id?: number;
  uuid?: string;
  name?: string;
  email?: string;
  initials?: string;
  profile_picture?: string | null;
  access?: string;
  // Legacy uppercase keys
  Name?: string;
  Email?: string;
}

export interface CalendarFilters {
  event_type?: CalendarEventType;
  source_type?: CalendarSourceType;
  status?: CalendarEventStatus;
  search?: string;
}

export interface CalendarState {
  events: CalendarEvent[];
  selectedEvent: CalendarEvent | null;
  loading: boolean;
  error: string | null;
  filters: CalendarFilters;
  feedToken: string | null;
  syncStatus: 'idle' | 'syncing' | 'success' | 'error';
}

// Redux action type interfaces
export type CalendarActionTypes =
  | { type: 'FETCH_CALENDAR_EVENTS'; payload: CalendarEvent[] }
  | { type: 'FETCH_CALENDAR_EVENTS_LOADING' }
  | { type: 'FETCH_CALENDAR_EVENTS_ERROR'; payload: string }
  | { type: 'SET_CALENDAR_EVENT'; payload: CalendarEvent }
  | { type: 'CREATE_CALENDAR_EVENT'; payload: CalendarEvent }
  | { type: 'UPDATE_CALENDAR_EVENT'; payload: CalendarEvent }
  | { type: 'DELETE_CALENDAR_EVENT'; payload: string }    // uuid
  | { type: 'SET_CALENDAR_FILTERS'; payload: Partial<CalendarFilters> }
  | { type: 'SET_CALENDAR_FEED_TOKEN'; payload: string }
  | { type: 'SET_CALENDAR_SYNC_STATUS'; payload: CalendarState['syncStatus'] }
  | { type: 'CLEAR_CALENDAR_EVENTS' };

/**
 * Visual identity per event_type, in brand tokens only.
 *
 * The old table was raw Tailwind palettes (`bg-blue-50` … `bg-rose-50`) with a
 * hand-paired `dark:` twin for every entry — fourteen palettes that belonged to
 * no design system and drifted from the app's surfaces in dark mode. The five
 * `chart-*` tokens plus the status ramps cover every type the backend emits and
 * flip with the theme on their own, so a chip needs no `dark:` variant.
 *
 * Types that mean the same thing to a reader deliberately share a style:
 * `court_date` and `statute_of_limitations` are both hard, unmissable dates,
 * so both read danger.
 */
export interface EventTypeStyle {
  /** Tinted chip fill for an event block. Low alpha — it sits on glass. */
  chip: string;
  /** Foreground for the chip's title/time and the type's label. */
  text: string;
  /** The legend / list marker dot — the one fully saturated use. */
  dot: string;
  /** Inset hairline ring around the chip. */
  ring: string;
}

export const EVENT_TYPE_COLORS: Record<CalendarEventType, EventTypeStyle> = {
  task:                    { chip: 'bg-chart-1/10',  text: 'text-chart-1',  dot: 'bg-chart-1',  ring: 'ring-chart-1/25' },
  task_deadline:           { chip: 'bg-chart-1/10',  text: 'text-chart-1',  dot: 'bg-chart-1',  ring: 'ring-chart-1/25' },
  matter:                  { chip: 'bg-chart-2/10',  text: 'text-chart-2',  dot: 'bg-chart-2',  ring: 'ring-chart-2/25' },
  matter_deadline:         { chip: 'bg-chart-2/10',  text: 'text-chart-2',  dot: 'bg-chart-2',  ring: 'ring-chart-2/25' },
  project:                 { chip: 'bg-chart-3/10',  text: 'text-chart-3',  dot: 'bg-chart-3',  ring: 'ring-chart-3/25' },
  project_deadline:        { chip: 'bg-chart-3/10',  text: 'text-chart-3',  dot: 'bg-chart-3',  ring: 'ring-chart-3/25' },
  hearing:                 { chip: 'bg-chart-4/10',  text: 'text-chart-4',  dot: 'bg-chart-4',  ring: 'ring-chart-4/25' },
  consultation:            { chip: 'bg-chart-5/10',  text: 'text-chart-5',  dot: 'bg-chart-5',  ring: 'ring-chart-5/25' },
  meeting:                 { chip: 'bg-success-500/10', text: 'text-success-700 dark:text-success-300', dot: 'bg-success-500', ring: 'ring-success-500/25' },
  client_meeting:          { chip: 'bg-info-500/10',    text: 'text-info-700 dark:text-info-300',       dot: 'bg-info-500',    ring: 'ring-info-500/25' },
  filing_deadline:         { chip: 'bg-warning-500/10', text: 'text-warning-700 dark:text-warning-300', dot: 'bg-warning-500', ring: 'ring-warning-500/25' },
  court_date:              { chip: 'bg-danger-500/10',  text: 'text-danger-700 dark:text-danger-300',   dot: 'bg-danger-500',  ring: 'ring-danger-500/25' },
  statute_of_limitations:  { chip: 'bg-danger-500/10',  text: 'text-danger-700 dark:text-danger-300',   dot: 'bg-danger-500',  ring: 'ring-danger-500/25' },
  reminder:                { chip: 'bg-champagne/12',   text: 'text-fg-body',  dot: 'bg-champagne', ring: 'ring-champagne/30' },
  custom:                  { chip: 'bg-surface-sunken/70', text: 'text-fg-body', dot: 'bg-fg-muted', ring: 'ring-hairline' },
};
